import { excludeNullOrUndefined } from 'checkout/utils'
import { DAY_MONTH_NAME_SHORT, INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import productLabels from 'i18n/productLabels'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { sum } from 'lib/array/arrayUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { createSelector } from 'reselect'
import { getExperienceMobileAppDiscountTotal } from '../experience'
import getExperienceItemsView from '../itemViews/getExperienceItemsView'

const getExperienceBreakdownView = createSelector(
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => state.checkout.commissionData,
  (state: App.State) => getExperienceMobileAppDiscountTotal(state),
  (viewWithStatus, commissionData, experienceMobileAppDiscountTotals): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    const visibleItems = viewWithStatus.data
    if (visibleItems.length === 0) { return { hasRequiredData: viewWithStatus.hasRequiredData, data: [] } }

    const intl = getCurrentIntl()
    const sep = intl.formatMessage(middleDotSeparatorMessage)
    const price = sum(visibleItems.map(i => i?.item.isAccomBundle ? 0 : (i?.totals.price ?? 0)))
    const memberPrice = sum(visibleItems.map(i => i?.item.isAccomBundle ? 0 : (i?.totals.memberPrice ?? 0)))
    const totalDiscount = !commissionData ? 0 : sum(visibleItems.map(expView => commissionData.itemsCommission[expView.id]?.itemDiscount ?? 0))

    const experienceBreakdownView: App.Checkout.PriceBreakdownView = {
      title: intl.formatMessage(productLabels.experiences),
      price: price + experienceMobileAppDiscountTotals.amount,
      memberPrice,
      additionalInfoText: [],
      ...(commissionData && {
        commission: {
          commissionPercentage: Number((totalDiscount / price * 100).toFixed(2)),
          itemDiscount: totalDiscount,
        },
      }),
      mobileAppDiscount: experienceMobileAppDiscountTotals,
      items: visibleItems.filter(excludeNullOrUndefined).map<App.Checkout.ExperienceItemBreakdownView>(expView => {
        const isBundled = !!expView.item.isAccomBundle
        const additionalInfo: Array<App.Checkout.ItemBreakdownViewAdditionalText> = expView.ticketSummaryViews
          .filter(ticketView => !ticketView.unavailable)
          .map(ticketView => ({
            text: intl.formatMessage(
              {
                id: 'e9GEKqY1P7UqFXbrNFK5',
                defaultMessage: '{count}x {ticketName}',
                description: 'Ticket quantity and name in experience checkout breakdown',
              },
              { count: ticketView.count, ticketName: ticketView.name },
            ),
            value: isBundled ? undefined : ticketView.price,
          }))

        if (expView.pickupPoint) {
          additionalInfo.push({
            text: intl.formatMessage(
              { id: '-DRNwa5R09t20NLZkhis', defaultMessage: 'Pick up: {pickupPointName}', description: 'Experience pickup point in checkout breakdown in the checkout flow' },
              { pickupPointName: expView.pickupPoint.name },
            ),
          })
        }

        const dateTimeLanguage = [
          expView.bookingDate && expView.bookingDate && moment.utc(expView.bookingDate).format(DAY_MONTH_NAME_SHORT),
          expView.bookingTime && expView.hasTimeslots && !expView.hideTimeSlots && intlDateFormatter(moment.utc(`${expView.bookingDate}T${expView.bookingTime}`).toDate(), INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale),
          expView.language?.name,
        ].filter(excludeNullOrUndefined)

        if (dateTimeLanguage.length > 0) {
          additionalInfo.push({ text: dateTimeLanguage.join(` ${sep} `) })
        }

        return {
          title: expView.title,
          price: isBundled ? 0 : expView.totals.price + (expView.totals.mobileAppDiscount?.amount ?? 0),
          additionalInfo,
          itemType: 'experience',
          offerId: expView.experienceId,
          itemId: expView.item.itemId,
          additionalElements: [],
          taxesAndFees: isBundled ? 0 : expView.totals.taxesAndFees,
          itemView: expView,
        }
      }),
    }

    return {
      hasRequiredData: true,
      data: [experienceBreakdownView],
    }
  },
)

export default getExperienceBreakdownView
